import React, { FC, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { track } from '@adsk/acs-web-analytics';
import A from '@adsk/alloy-react-a';
import EmptyState from '@adsk/alloy-react-empty-state';
import {
  AnalyticsEvents,
  NO_PERMISSIONS_HELP_ARTICLE,
  InsightProductName,
  AccountAdminProductName,
} from '../../constants';
import i18n from '../../helpers/i18n';
import { getAccountAdminUrl, getInsightUrl } from '../../utils';

type ProjectListEmptyStateProps = {
  accountAdminId?: string;
  executiveId?: string;
  canCreateProject: boolean;
  isTemplate?: boolean;
};

const ProjectListEmptyState: FC<ProjectListEmptyStateProps> = ({
  accountAdminId,
  executiveId,
  canCreateProject,
  isTemplate = false,
}) => {
  const isExecutive = !!executiveId;
  const isAccountAdmin = !!accountAdminId;

  const accountAdminLink = isAccountAdmin
    ? getAccountAdminUrl(accountAdminId, isTemplate)
    : undefined;
  const insightLink = isExecutive ? getInsightUrl(executiveId) : undefined;
  const helpSiteLink = NO_PERMISSIONS_HELP_ARTICLE;

  const translationsType = useMemo(
    () => ({
      typeSingle: isTemplate
        ? i18n.t('PROJECT_TEMPLATE_INLINE')
        : i18n.t('PROJECT_INLINE'),
      typeMany: isTemplate
        ? i18n.t('PROJECT_TEMPLATES_INLINE')
        : i18n.t('PROJECTS_INLINE'),
    }),
    [isTemplate]
  );

  return (
    <EmptyState
      data-testid="ProjectListEmptyState"
      illustrationType={
        EmptyState.ILLUSTRATION_TYPES.BUILDING_CONSTRUCTION_GREY
      }
      title={
        isAccountAdmin || (isExecutive && !canCreateProject)
          ? i18n.t('EMPTY_STATE_NOT_A_MEMBER', translationsType)
          : i18n.t('EMPTY_STATE_NO_PROJECTS_TO_DISPLAY', translationsType)
      }
    >
      <span data-testid="EmptyState.CTA">
        {canCreateProject &&
          !isAccountAdmin &&
          i18n.t('EMPTY_STATE_GET_STARTED', translationsType)}

        {isExecutive && !canCreateProject && !isAccountAdmin && (
          <Trans
            i18nKey="EMPTY_STATE_GO_TO_INSIGHT"
            values={{
              link: `<InsightLink>${i18n.t('INSIGHT')}</InsightLink>`,
              ...translationsType,
            }}
            components={{
              InsightLink: (
                <A
                  data-testid="InsightLinkCTA"
                  href={insightLink}
                  onClick={() =>
                    track(AnalyticsEvents.navigateToOtherProduct, {
                      product_name: InsightProductName,
                    })
                  }
                />
              ),
            }}
          />
        )}

        {isAccountAdmin && (
          <Trans
            i18nKey="EMPTY_STATE_GO_TO_ACCOUNT_ADMIN"
            values={{
              link: `<AccountAdminLink>${i18n.t(
                'ACCOUNT_ADMINISTRATION'
              )}</AccountAdminLink>`,
              ...translationsType,
            }}
            components={{
              AccountAdminLink: (
                <A
                  data-testid="AccountAdministrationLinkCTA"
                  href={accountAdminLink}
                  onClick={() =>
                    track(AnalyticsEvents.navigateToOtherProduct, {
                      product_name: AccountAdminProductName,
                    })
                  }
                />
              ),
            }}
          />
        )}
        {!canCreateProject && !isAccountAdmin && !isExecutive && (
          <Trans
            i18nKey="EMPTY_STATE_CONTACT_ADMIN"
            values={{
              link: `<HelpSiteLink>${i18n.t('HELP_SITE')}</HelpSiteLink>`,
              ...translationsType,
            }}
            components={{
              HelpSiteLink: (
                <A
                  data-testid="NoPermissionCTA"
                  href={helpSiteLink}
                  onClick={() =>
                    track(AnalyticsEvents.navigateToNoPermissionsHelpArticle)
                  }
                />
              ),
            }}
          />
        )}
      </span>
    </EmptyState>
  );
};

export default ProjectListEmptyState;
